import React, { useState, useEffect, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";
import axiosInstance from "../AxiosInstance";
import SidebarLayout from "../SidebarLayout";
import { Box, Container, GlobalStyles } from "@mui/material";
import { useNotification } from "../NotificationContext";
import ADSearchForm from "./ADSearchForm";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";

const AD_CALENDAR_AHA_CACHE_KEY = "adcalendar_aha_state_v1";

/** Mount node class — FullCalendar v6 does not render `.fc-button-text` when custom button `text` is "". */
const AHA_FC_REFRESH_MOUNT_CLASS = "aha-fc-refresh-root";

/** Inline SVG so createRoot works without MUI theme; uses currentColor for FC toolbar (--fc-button-text-color). */
function AhaToolbarRefreshIcon({ spinning }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      focusable="false"
      style={{
        display: "block",
        animation: spinning ? "ahaWorkflowRefreshSpin 0.85s linear infinite" : "none",
      }}
    >
      <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  );
}

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(AD_CALENDAR_AHA_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const ADCalendarAHAMain = () => {
  const cached = readCache();
  const { showNotification } = useNotification();
  const [selectedVenue, setSelectedVenue] = useState(cached.selectedVenue ?? "");
  const [currentvenue, setCurrentvenue] = useState(cached.currentvenue ?? "");
  const [Courses, setCourses] = useState(Array.isArray(cached.courses) ? cached.courses : []);
  const [venues, setVenues] = useState([]);
  const [calendarDate, setCalendarDate] = useState(
    cached.calendarDate || dayjs().format("YYYY-MM-DD"),
  );
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [ahaBlsCourseCount, setAhaBlsCourseCount] = useState(null);
  const [venueWorkflowStatusById, setVenueWorkflowStatusById] = useState({});
  const [workflowStatusRefreshing, setWorkflowStatusRefreshing] = useState(false);
  const refreshToolbarIconRootRef = useRef(null);
  const [userRole, setUserRole] = useState(null);
  const [icpisname, setIcpisname] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const stored = localStorage.getItem("token");
    if (!stored) return;
    try {
      const decodedToken = jwtDecode(stored);
      setUserRole(decodedToken.claims.role);
      setIcpisname(decodedToken.claims.icpiename ?? decodedToken.claims.firstname);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchVenueWorkflowStatuses = useCallback(() => {
    if (!token || !userRole) {
      setVenueWorkflowStatusById({});
      return Promise.resolve();
    }
    const month = dayjs(calendarDate).format("YYYY-MM");
    return axiosInstance
      .get("/course/aha-venue-workflow-statuses", {
        params: { month, role: userRole, icpisname: icpisname ?? "" },
      })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const map = {};
        list.forEach((row) => {
          if (row && row.venueId) {
            map[row.venueId] = row.status;
          }
        });
        setVenueWorkflowStatusById(map);
      })
      .catch(() => setVenueWorkflowStatusById({}));
  }, [calendarDate, token, userRole, icpisname]);

  const refreshWorkflowStatusesWithSpinner = useCallback(() => {
    setWorkflowStatusRefreshing(true);
    Promise.resolve(fetchVenueWorkflowStatuses()).finally(() => {
      setWorkflowStatusRefreshing(false);
    });
  }, [fetchVenueWorkflowStatuses]);

  const ensureRefreshToolbarMountEl = useCallback(() => {
    const btn = document.querySelector(".fc-refreshVenueStatus-button");
    if (!btn) {
      return null;
    }
    let span = btn.querySelector(`.${AHA_FC_REFRESH_MOUNT_CLASS}`);
    if (!span) {
      span = document.createElement("span");
      span.className = AHA_FC_REFRESH_MOUNT_CLASS;
      btn.appendChild(span);
    }
    return span;
  }, []);

  const renderRefreshToolbarIcon = useCallback(() => {
    const mountEl = ensureRefreshToolbarMountEl();
    if (!mountEl) {
      return;
    }
    const icon = <AhaToolbarRefreshIcon spinning={workflowStatusRefreshing} />;
    if (!refreshToolbarIconRootRef.current) {
      refreshToolbarIconRootRef.current = createRoot(mountEl);
    }
    refreshToolbarIconRootRef.current.render(icon);
  }, [ensureRefreshToolbarMountEl, workflowStatusRefreshing]);

  const tearDownRefreshToolbarIcon = useCallback(() => {
    if (refreshToolbarIconRootRef.current) {
      try {
        refreshToolbarIconRootRef.current.unmount();
      } catch {
        /* ignore */
      }
      refreshToolbarIconRootRef.current = null;
    }
    document
      .querySelector(".fc-refreshVenueStatus-button")
      ?.querySelector(`.${AHA_FC_REFRESH_MOUNT_CLASS}`)
      ?.remove();
  }, []);

  const handleCalendarDatesSet = useCallback(
    (arg) => {
      const d =
        arg?.view?.currentStart instanceof Date
          ? dayjs(arg.view.currentStart).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD");
      tearDownRefreshToolbarIcon();
      setCalendarDate(d);
      requestAnimationFrame(() => {
        renderRefreshToolbarIcon();
      });
    },
    [tearDownRefreshToolbarIcon, renderRefreshToolbarIcon],
  );

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 12;
    const tryRender = () => {
      if (cancelled) return;
      renderRefreshToolbarIcon();
      if (!document.querySelector(`.${AHA_FC_REFRESH_MOUNT_CLASS}`) && attempt < maxAttempts) {
        attempt += 1;
        requestAnimationFrame(tryRender);
      }
    };
    const t = setTimeout(tryRender, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [renderRefreshToolbarIcon]);

  useEffect(() => {
    return () => {
      tearDownRefreshToolbarIcon();
    };
  }, [tearDownRefreshToolbarIcon]);

  useEffect(() => {
    fetchVenueWorkflowStatuses();
  }, [fetchVenueWorkflowStatuses]);

  useEffect(() => {
    if (!userRole) return;
    const authHeader = "Bearer " + token;
    axiosInstance
      .get("/course/coursevenueIdaddress", {
        params: {
          role: userRole,
          icpisname: icpisname ?? "",
        },
        headers: {
          Authorization: authHeader,
        },
      })
      .then((response) => {
        const list = Array.isArray(response.data) ? response.data : [];
        setVenues(list);
        if (selectedVenue) {
          const match = list.find((venue) => venue.id === selectedVenue);
          if (match) {
            setCurrentvenue(match);
          }
        }
      })
      .catch(() => {
        showNotification("Error fetching venues!", "error");
      });
  }, [userRole, icpisname]);

  const loadAhaCalendarCourses = useCallback(
    (venueId, { showEmptyWarning = false } = {}) => {
      if (!venueId || !token) {
        return Promise.resolve();
      }
      const authHeader = "Bearer " + token;
      return axiosInstance
        .get("/course/coursecalendaraha", {
          params: { venueId },
          headers: { Authorization: authHeader },
        })
        .then((response) => {
          const list = Array.isArray(response.data) ? response.data : [];
          setCourses(list);
          setAhaBlsCourseCount(list.length);
          if (showEmptyWarning && list.length === 0) {
            showNotification("No courses found for this venue!", "warning");
          }
        })
        .catch((error) => {
          console.error("Error loading AHA calendar:", error);
          setCourses([]);
          setAhaBlsCourseCount(null);
          if (showEmptyWarning) {
            showNotification("Error in course search!", "error");
          }
        });
    },
    [token, showNotification],
  );

  useEffect(() => {
    if (!currentvenue?.id) {
      setCourses([]);
      setAhaBlsCourseCount(null);
      return;
    }
    loadAhaCalendarCourses(currentvenue.id, { showEmptyWarning: false });
  }, [currentvenue?.id, loadAhaCalendarCourses]);

  const handleReset = () => {
    setSelectedVenue("");
    setCurrentvenue("");
    setCourses([]);
    setAhaBlsCourseCount(null);
    showNotification("Reset successfully!", "success");
  };

  const handleSearch = () => {
    if (!currentvenue.id) {
      showNotification("Please select a venue before searching!", "warning");
      return;
    }
    loadAhaCalendarCourses(currentvenue.id, { showEmptyWarning: true });
  };

  const handleNotify = async () => {
    if (!currentvenue?.id) {
      showNotification("Please select a venue before notifying!", "warning");
      return;
    }
    const month = dayjs(calendarDate).format("YYYY-MM");
    setNotifyLoading(true);
    try {
      const res = await axiosInstance.post("/course/aha-notify", {
        venueId: currentvenue.id,
        month,
      });
      const payload = res.data;
      const code = payload != null ? Number(payload.code) : NaN;
      if (res.status === 200 && code === 0) {
        showNotification("Notification success", "success");
        setWorkflowStatusRefreshing(true);
        Promise.resolve(fetchVenueWorkflowStatuses()).finally(() => setWorkflowStatusRefreshing(false));
      } else {
        showNotification(payload?.message || "Notify failed", "error");
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Notify failed";
      showNotification(msg, "error");
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!currentvenue?.id) {
      showNotification("Please select a venue before confirming!", "warning");
      return;
    }
    const month = dayjs(calendarDate).format("YYYY-MM");
    setConfirmLoading(true);
    try {
      const res = await axiosInstance.post("/course/aha-confirm", {
        venueId: currentvenue.id,
        month,
      });
      const payload = res.data;
      const code = payload != null ? Number(payload.code) : NaN;
      if (res.status === 200 && code === 0) {
        showNotification("Confirmed", "success");
        setWorkflowStatusRefreshing(true);
        Promise.resolve(fetchVenueWorkflowStatuses()).finally(() => setWorkflowStatusRefreshing(false));
      } else {
        showNotification(payload?.message || "Confirm failed", "error");
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Confirm failed";
      showNotification(msg, "error");
    } finally {
      setConfirmLoading(false);
    }
  };

  const calendarEvents = Courses.map((course) => {
    const active = course.active ?? course.isActive;
    return {
      title: `${course.courseTitle}`,
      start: `${course.date}T${course.startTime}`,
      end: `${course.date}T${course.endTime}`,
      price: `${course.price}`,
      extendedProps: {
        price: `${course.price}`,
        backgroundColor: active ? "green" : "red",
      },
    };
  });

  const renderEventContent = (eventInfo) => {
    return (
      <div
        style={{
          backgroundColor: eventInfo.event.extendedProps.backgroundColor,
          padding: "5px",
          borderRadius: "5px",
          color: "white",
        }}
      >
        <b>{eventInfo.timeText}</b>
        <br />
        <span>{eventInfo.event.title}</span>
        <br />
        <span>Price: ${eventInfo.event.extendedProps.price}</span>
      </div>
    );
  };

  useEffect(() => {
    try {
      sessionStorage.setItem(
        AD_CALENDAR_AHA_CACHE_KEY,
        JSON.stringify({
          selectedVenue,
          currentvenue,
          courses: Courses,
          calendarDate,
        }),
      );
    } catch {
      // ignore
    }
  }, [selectedVenue, currentvenue, Courses, calendarDate]);

  return (
    <SidebarLayout>
      <GlobalStyles
        styles={{
          "@keyframes ahaWorkflowRefreshSpin": {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
          },
        }}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <ADSearchForm
          handleSearch={handleSearch}
          handleReset={handleReset}
          Courses={Courses}
          venues={venues}
          setCurrentvenue={setCurrentvenue}
          selectedVenue={selectedVenue}
          setSelectedVenue={setSelectedVenue}
          showNotifyButton
          onNotify={handleNotify}
          notifyDisabled={notifyLoading || confirmLoading}
          venueWorkflowStatusById={venueWorkflowStatusById}
          showConfirmButton
          onConfirm={handleConfirm}
          confirmDisabled={notifyLoading || confirmLoading}
          showPriceTable={false}
          ahaBlsCourseCount={ahaBlsCourseCount}
        />
        <Box
          sx={{
            "& .fc-refreshVenueStatus-button": {
              padding: "4px 8px",
              minWidth: 40,
              verticalAlign: "middle",
            },
            [`& .fc-refreshVenueStatus-button .${AHA_FC_REFRESH_MOUNT_CLASS}`]: {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 0,
              color: "var(--fc-button-text-color, #fff)",
            },
          }}
        >
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={calendarDate}
            events={calendarEvents}
            customButtons={{
              refreshVenueStatus: {
                text: "",
                hint: "Refresh venue status",
                click: () => {
                  refreshWorkflowStatusesWithSpinner();
                },
              },
            }}
            datesSet={handleCalendarDatesSet}
            headerToolbar={{
              left: "prev,next today refreshVenueStatus",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            eventContent={renderEventContent}
            eventClick={(info) => {
              alert("Course: " + info.event.title);
            }}
            height="auto"
          />
        </Box>
      </Container>
    </SidebarLayout>
  );
};

export default ADCalendarAHAMain;
